import React, { useState } from 'react';
import {
  Dialog,
  Button,
  Flex,
  Text,
  TextArea,
  TextField,
} from '@radix-ui/themes';
import { useImportAbiMutation } from '../../graphql/mutations/ImportAbi.generated';

interface ImportAbiModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

const ImportAbiModal: React.FC<ImportAbiModalProps> = ({
  open,
  onOpenChange,
  onSuccess,
}) => {
  const [name, setName] = useState('');
  const [abi, setAbi] = useState('');
  const [error, setError] = useState('');

  const [importAbiMutation, { loading }] = useImportAbiMutation();

  const resetForm = () => {
    setName('');
    setAbi('');
    setError('');
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      resetForm();
    }
    onOpenChange(newOpen);
  };

  const validateAbi = (abiString: string): boolean => {
    try {
      const parsed = JSON.parse(abiString);
      return Array.isArray(parsed);
    } catch {
      return false;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!name.trim()) {
      setError('Name is required');
      return;
    }

    if (!abi.trim()) {
      setError('ABI is required');
      return;
    }

    if (!validateAbi(abi)) {
      setError('Invalid ABI format. Must be valid JSON array');
      return;
    }

    try {
      await importAbiMutation({
        variables: {
          input: {
            name: name.trim(),
            abiJson: abi,
          },
        },
      });

      handleOpenChange(false);

      if (onSuccess) {
        onSuccess();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to import ABI');
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={handleOpenChange}>
      <Dialog.Content style={{ maxWidth: 600 }}>
        <Dialog.Title>Import Contract ABI</Dialog.Title>
        <Dialog.Description size="2" mb="4">
          Import an ABI to enable better verification and interaction. ABIs can
          be shared across multiple contracts.
        </Dialog.Description>

        <form onSubmit={handleSubmit}>
          <Flex direction="column" gap="4">
            <label>
              <Text as="div" size="2" mb="1" weight="bold">
                Name
              </Text>
              <TextField.Root
                placeholder="e.g., USDC, UniswapV2Router..."
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={loading}
              />
            </label>

            <label>
              <Text as="div" size="2" mb="1" weight="bold">
                ABI (JSON)
              </Text>
              <TextArea
                placeholder='[{"type":"function","name":"transfer",...}]'
                value={abi}
                onChange={(e) => setAbi(e.target.value)}
                disabled={loading}
                rows={10}
                style={{ fontFamily: 'monospace', fontSize: '12px' }}
              />
            </label>

            {error && (
              <Text color="red" size="2">
                {error}
              </Text>
            )}

            <Flex gap="3" mt="4" justify="end">
              <Dialog.Close>
                <Button variant="soft" color="gray" disabled={loading}>
                  Cancel
                </Button>
              </Dialog.Close>
              <Button type="submit" disabled={loading}>
                {loading ? 'Importing...' : 'Import'}
              </Button>
            </Flex>
          </Flex>
        </form>
      </Dialog.Content>
    </Dialog.Root>
  );
};

export default ImportAbiModal;
